import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {AnagraficaContext, AppContext} from "../../../../../App";
import {
	addRelazione,
	getAllRelazioniBySoggetto,
	searchAnagrafica,
	setAnagrafica
} from "../../../../../services/AnagraficaServices";
import { Alert, AlertTitle, Skeleton } from '@mui/material';
import {
	Accordion,
	AccordionActions,
	AccordionDetails, AccordionSummary,
	Button,
	Card, CardHeader, IconButton,
	Typography
} from "@mui/material";
import clsx from "clsx";
import {BaseStyle} from "../../../../../styles/shared/BaseStyle";
import AnagraficaCard from "../AnagraficaCard";
import {useHistory} from "react-router-dom";
import Grid from "@mui/material/Grid";
import SearchCardResult from "../../utils/SearchCardResult";
import {AnagraficaCompleta} from "../../../../widgets/common/AnagraficaCompleta";
import {CustomAutoComplete} from "../../../../widgets/inputs/CustomAutoComplete";
import {relations} from "../../../../../utils/dictionaries/Generics";
import useAnagraficaSearch from "../../utils/useAnagraficaSearch";
import {clean} from "../../../../../utils/GenericUtils";
import {useForm, FormProvider} from "react-hook-form";
import {resetAnagrafica} from "../../../interviste/intervista/utils/ResetUtils";
import SearchPaper from "../../utils/SearchPaper";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {addAnagrafica} from "../../../../../services/TicketServices";
import {setOpenSnackbar} from "../../../../../services/AppServices";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";


const RelazioniSection = ({idSoggetto}) => {
	const classes = BaseStyle();
	const history = useHistory();
	const anagraficaContext = useContext(AnagraficaContext);
	const appContext = useContext(AppContext);
	const methods = useForm({mode: "onChange"});
	const [value, setInputValue] = useState("");
	const [open, setOpen] = useState(false);
	const [pageNumber, setPageNumber] = useState(0);
	const {loadingSearch, errorSearch, anagraficheSearch, hasMoreSearch} = useAnagraficaSearch(value, pageNumber);
	const observer = useRef();
	const [anagraficaSelected, setAnagraficaSelected] = useState({});
	const [expanded, setExpanded] = useState(true);
	
	const lastTicketElementRef = useCallback(node => {
		if (loadingSearch) return
		if (observer.current) observer.current.disconnect()
		observer.current = new IntersectionObserver(entries => {
			if (entries[0].isIntersecting && hasMoreSearch) {
				setPageNumber(prevState => prevState + 1);
			}
		})
		if (node) observer.current.observe(node)
	}, [loadingSearch, hasMoreSearch])
	
	const handleSearch = (event) => {
		setInputValue(event.target.value);
	}
	
	const onSearch = useCallback(() => {
		if (open) {
			let data = methods.getValues()?.["search"]
			searchAnagrafica(clean(data), anagraficaContext.dispatch);
		}
	}, [methods, open, anagraficaContext.dispatch])
	
	const onClickDetail = (anagrafica) => {
		history.push("/gestione/pazienti/" + anagrafica.idSoggetto)
	}
	
	const onAnagraficaSelect = (anagrafica) => {
		setAnagraficaSelected(anagrafica);
		document.getElementById("anagrafica-grid-codf")?.scrollIntoView({behavior: 'smooth', block: 'center'})
		let value = resetAnagrafica(anagrafica);
		methods.reset(value);
	}
	
	const onSubmit = useCallback((data) => {
		addAnagrafica(data, (id) => {
			setAnagrafica({
				...data,
				idSoggetto: id
			}, anagraficaContext.dispatch)
			addRelazione(id, idSoggetto, data?.["tipoRelazione"], anagraficaContext.dispatch)
		}).then(() => {
			setOpenSnackbar("success", "Relazione aggiunta con successo!", appContext.dispatch)
		})
	}, [anagraficaContext.dispatch, appContext.dispatch, idSoggetto])
	
	useEffect(() => {
		getAllRelazioniBySoggetto(idSoggetto, anagraficaContext.dispatch);
	}, [idSoggetto, anagraficaContext.dispatch, expanded])
	
	return (
		<React.Fragment>
			<Accordion
				className={classes.roundAccordion}
				style={{borderRadius: 24, marginBottom: 20}}
				expanded={expanded}
				onChange={() => setExpanded(!expanded)}
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon/>}
				>
					<Typography className={classes.heading}>
						<b>Crea una relazione cercando o inserendo un'anagrafica ed indicandone il tipo</b>
					</Typography>
				</AccordionSummary>
				<FormProvider {...methods}>
					<AccordionDetails>
						<Grid container spacing={3}>
							
							<SearchPaper value={value} open={open} onSearch={onSearch} handleSearch={handleSearch}
							             setOpen={setOpen}/>
							
							<Grid item xs={12}>
								<SearchCardResult open={open} onClickDetail={onAnagraficaSelect} anagraficheSearch={anagraficheSearch}
								                  errorSearch={errorSearch} loadingSearch={loadingSearch}
								                  lastTicketElementRef={lastTicketElementRef} color={true}/>
							</Grid>
							
							<AnagraficaCompleta classes={classes} intro={"false"} moreInfo={true} anagrafica={anagraficaSelected}/>
							<Grid item xs={12}>
								<CustomAutoComplete
									name={"tipoRelazione"}
									label={"Tipo di relazione *"}
									control={methods.control}
									options={[...Object.keys(relations).sort(), ""]}
									multiple={false}
									rules={{required: true}}
									error={methods.errors["tipoRelazione"]}
								/>
							</Grid>
						</Grid>
					</AccordionDetails>
					<AccordionActions className={classes.flexRight}>
						<Button size={"small"} color={"primary"} variant={"contained"} className={classes.roundButton}
						        style={{borderRadius: 24}}
						        disableElevation onClick={methods.handleSubmit(onSubmit)}>
							<b>Crea relazione</b>
						</Button>
					</AccordionActions>
				</FormProvider>
			</Accordion>
			{
				anagraficaContext.state.loadingGetRelazioni ? <Skeleton/> :
					anagraficaContext.state.errorGetRelazioni !== "" ? <Alert severity="error" style={{borderRadius: 24}}>
							<AlertTitle>Errore</AlertTitle>
							Prova ad effettuare un refresh della pagina entro 5 minuti. Se l'errore persiste contatta gli
							sviluppatori.
						</Alert> :
						anagraficaContext.state.relazioni.length === 0 ? <Alert severity="info" style={{borderRadius: 24}}>
								<AlertTitle>😕</AlertTitle>
								Non ha relazioni con nessuno questo paziente ...
							</Alert> :
							anagraficaContext.state.relazioni.map((relazione, index) => {
								return (
									<Card key={index} className={clsx(classes.roundBorder, classes.mailCard)}>
										<CardHeader
											style={{padding: 2}}
											title={
												<Typography variant={"subtitle1"} color={"primary"} style={{paddingLeft: 15, paddingTop: 15}}>
													<b>{relazione.tipoRelazione?.toUpperCase()}</b>
												</Typography>
											}
											action={
												<IconButton style={{marginRight: 20, marginTop: 10}} size={"small"}
												            onClick={() => alert("Cancellerò la relazione con ID: " + relazione.id)}>
													<DeleteTwoToneIcon/>
												</IconButton>
											}
										/>
										<AnagraficaCard anagrafica={relazione.dst} onClickDetail={onClickDetail}/>
									</Card>
								)
							})
			}
		</React.Fragment>
	);
}

export default React.memo(RelazioniSection)
